import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowUpRight } from "lucide-react";
import { SITE } from "@/config";
import { Fade } from "@/components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Kicker = ({ label }) => (
  <Fade className="mb-10 flex items-center gap-4 font-mono text-[11px] uppercase tracking-[0.3em] text-ink/45">
    <span className="text-accent-dark">—</span>
    <span className="h-px w-10 bg-ink/20" aria-hidden="true" />
    <span>{label}</span>
  </Fade>
);

const EMPTY = {
  nome: "",
  cognome: "",
  azienda: "",
  ruolo: "",
  email: "",
  telefono: "",
  numero_collaboratori: "",
  messaggio: "",
  privacy: false,
};

const inputClass =
  "w-full border border-ink/20 bg-white px-4 py-3.5 text-sm text-ink outline-none transition-colors duration-300 focus:border-accent";

const Field = ({ label, name, type = "text", required = false, value, onChange, testid }) => (
  <div>
    <label htmlFor={name} className="mb-2 block font-mono text-[10px] uppercase tracking-[0.25em] text-ink/50">
      {label}
      {required && " *"}
    </label>
    <input
      id={name}
      name={name}
      type={type}
      required={required}
      value={value}
      onChange={onChange}
      data-testid={testid}
      className={inputClass}
    />
  </div>
);

const ContactPage = () => {
  const [form, setForm] = useState(EMPTY);
  const [sending, setSending] = useState(false);

  const onChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((f) => ({ ...f, [name]: type === "checkbox" ? checked : value }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/leads`, form);
      toast.success("Richiesta inviata correttamente. Ti ricontatterò personalmente.");
      setForm(EMPTY);
    } catch {
      toast.error(
        "Non è stato possibile inviare la richiesta. Riprova oppure contattami direttamente su WhatsApp.",
        {
          action: {
            label: "WhatsApp",
            onClick: () => window.open(`https://wa.me/${SITE.whatsapp}`, "_blank", "noopener"),
          },
        }
      );
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="contact-page" className="bg-fog pt-[72px] text-ink">
      <div className="mx-auto max-w-[1440px] px-6 py-20 lg:px-12 lg:py-32">
        <div className="grid gap-16 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <Kicker label="Contatti" />
            <Fade>
              <h1 className="font-display text-4xl font-extrabold leading-[1.05] tracking-tighter sm:text-5xl lg:text-6xl">
                Raccontami la tua azienda.
              </h1>
              <p className="mt-8 max-w-md text-base leading-relaxed text-ink/60 md:text-lg">
                Il primo passo è un confronto senza impegno: capire dove si trova oggi la tua azienda
                e cosa ne sta limitando la crescita.
              </p>
            </Fade>
            <Fade delay={0.1} className="mt-12 space-y-6 border-t border-ink/10 pt-8">
              <div>
                <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-ink/40">Email</p>
                <a
                  href={`mailto:${SITE.email}`}
                  data-testid="contact-email"
                  className="mt-1 block text-base font-medium text-ink transition-colors duration-300 hover:text-accent-dark"
                >
                  {SITE.email}
                </a>
              </div>
              <div>
                <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-ink/40">WhatsApp</p>
                <a
                  href={`https://wa.me/${SITE.whatsapp}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="contact-whatsapp"
                  className="mt-1 block text-base font-medium text-ink transition-colors duration-300 hover:text-accent-dark"
                >
                  {SITE.whatsappLabel}
                </a>
              </div>
              <div className="flex gap-6 pt-4">
                {Object.entries(SITE.socials).map(([k, v]) => (
                  <a
                    key={k}
                    href={v}
                    data-testid={`contact-social-${k}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-mono text-[10px] uppercase tracking-[0.25em] text-ink/50 transition-colors duration-300 hover:text-accent-dark"
                  >
                    {k}
                  </a>
                ))}
              </div>
            </Fade>
          </div>

          <div className="lg:col-span-7">
            <Fade delay={0.1}>
              <form onSubmit={onSubmit} data-testid="contact-form" className="grid gap-6 sm:grid-cols-2">
                <Field label="Nome" name="nome" required value={form.nome} onChange={onChange} testid="form-nome" />
                <Field label="Cognome" name="cognome" required value={form.cognome} onChange={onChange} testid="form-cognome" />
                <Field label="Azienda" name="azienda" value={form.azienda} onChange={onChange} testid="form-azienda" />
                <Field label="Ruolo" name="ruolo" value={form.ruolo} onChange={onChange} testid="form-ruolo" />
                <Field label="Email" name="email" type="email" required value={form.email} onChange={onChange} testid="form-email" />
                <Field label="Telefono" name="telefono" type="tel" value={form.telefono} onChange={onChange} testid="form-telefono" />
                <div className="sm:col-span-2">
                  <label htmlFor="numero_collaboratori" className="mb-2 block font-mono text-[10px] uppercase tracking-[0.25em] text-ink/50">
                    Numero collaboratori
                  </label>
                  <select
                    id="numero_collaboratori"
                    name="numero_collaboratori"
                    value={form.numero_collaboratori}
                    onChange={onChange}
                    data-testid="form-collaboratori"
                    className={inputClass}
                  >
                    <option value="">Seleziona</option>
                    <option value="1-5">1 – 5</option>
                    <option value="6-15">6 – 15</option>
                    <option value="16-50">16 – 50</option>
                    <option value="50+">Oltre 50</option>
                  </select>
                </div>
                <div className="sm:col-span-2">
                  <label htmlFor="messaggio" className="mb-2 block font-mono text-[10px] uppercase tracking-[0.25em] text-ink/50">
                    Qual è oggi la principale difficoltà della tua azienda? *
                  </label>
                  <textarea
                    id="messaggio"
                    name="messaggio"
                    required
                    rows={5}
                    value={form.messaggio}
                    onChange={onChange}
                    data-testid="form-messaggio"
                    className={`${inputClass} resize-none`}
                  />
                </div>
                <div className="sm:col-span-2">
                  <label className="flex cursor-pointer items-start gap-3 text-sm text-ink/60">
                    <input
                      type="checkbox"
                      name="privacy"
                      required
                      checked={form.privacy}
                      onChange={onChange}
                      data-testid="form-privacy"
                      className="mt-1 h-4 w-4 shrink-0 accent-[#9F761A]"
                    />
                    <span>
                      Ho letto e accetto la{" "}
                      <a href="/privacy-policy" className="underline underline-offset-4 hover:text-accent-dark">
                        Privacy Policy
                      </a>{" "}
                      e acconsento al trattamento dei dati per essere ricontattato. *
                    </span>
                  </label>
                </div>
                <div className="sm:col-span-2">
                  <button
                    type="submit"
                    disabled={sending}
                    data-testid="contact-submit"
                    className="btn-gold group inline-flex items-center gap-3 px-8 py-4 font-mono text-[11px] font-semibold uppercase tracking-[0.22em] disabled:opacity-50"
                  >
                    {sending ? "Invio in corso…" : "Richiedi un primo confronto"}
                    <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
                  </button>
                </div>
              </form>
            </Fade>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactPage;
